import React, { useState } from "react";
import { Smile, Meh, Frown } from "lucide-react";
import { toast } from "sonner";
import { logEventFeedback } from "@/lib/activitiesApi";
import { useAuth } from "@/context/AuthContext";

// ─────────────────────────────────────────────────────────────────────
// FeedbackChip — appears on event rows whose event.activity_id is set
// AND whose start time has passed. One tap → 😊/😐/😞 logged onto the
// activity's feedback_log. Hidden once logged.
//
// Respects the user's `activity_feedback_enabled` preference (passed
// as `enabled` prop by the caller — keeps this component pure).
// ─────────────────────────────────────────────────────────────────────

const OPTIONS = [
  { key: "positive", Icon: Smile, color: "#10b981", label: "Went well" },
  { key: "neutral",  Icon: Meh,   color: "#f59e0b", label: "Was okay" },
  { key: "negative", Icon: Frown, color: "#ef4444", label: "Tough one" },
];

export function FeedbackChip({ event, enabled = true, onLogged }) {
  const { family } = useAuth();
  const [logged, setLogged] = useState(event?.feedback_sentiment || null);
  const [busy, setBusy] = useState(false);

  // Visibility rules — keep pure / cheap.
  if (!enabled) return null;
  if (!event?.activity_id) return null;
  const now = Date.now();
  const end = event.end_at ? Date.parse(event.end_at) : Date.parse(event.start_at);
  if (Number.isNaN(end) || end > now) return null; // not finished yet
  if (logged) {
    const opt = OPTIONS.find((o) => o.key === logged);
    if (!opt) return null;
    return (
      <span
        data-testid={`feedback-logged-${event.id}`}
        className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground"
      >
        <opt.Icon className="w-3.5 h-3.5" style={{ color: opt.color }} />
        Logged
      </span>
    );
  }

  const onPick = async (key) => {
    setBusy(true);
    try {
      await logEventFeedback(family.id, event.id, key);
      setLogged(key);
      toast.success("Thanks for the feedback");
      onLogged?.(key);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't log feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      data-testid={`feedback-chip-${event.id}`}
      className="inline-flex items-center gap-0.5"
      onClick={(e) => { e.stopPropagation(); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {OPTIONS.map(({ key, Icon, color, label }) => (
        <button
          key={key}
          type="button"
          disabled={busy}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick(key); }}
          data-testid={`feedback-chip-${event.id}-${key}`}
          aria-label={label}
          className="w-7 h-7 inline-flex items-center justify-center rounded-full hover:bg-secondary/60 tap disabled:opacity-40"
        >
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </button>
      ))}
    </span>
  );
}
